import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_LEVEL, ValidationErrorResponse } from '@wyz/types';
import { UseFormGetFieldState } from 'react-hook-form';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { CommonErrorCodesEnumType } from '~/common/types';
import { BrandTemplateFormBase } from '~/connect/components';
import { QuoteBrandTemplateHelpers } from '~/connect/helpers';
import { quoteBrandTemplateFormSchema } from '~/connect/schemas';
import {
  ICriteria,
  IQuoteBrandTemplateForm,
  IQuoteBrandTemplatePayload,
} from '~/connect/types';

const DEFAULT_VALUES: IQuoteBrandTemplateForm = {
  default: false,
  name: '',
  criteria: [],
  brands: [],
  customerScope: [],
};

type Props =
  | {
      defaultValues?: Partial<IQuoteBrandTemplateForm>;
      mode?: 'CREATE' | 'UPDATE';
      isLoading: boolean;
      onSubmit: (
        data: IQuoteBrandTemplatePayload,
        formState: {
          criteria: ReturnType<
            UseFormGetFieldState<IQuoteBrandTemplatePayload>
          >;
        },
      ) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: Partial<IQuoteBrandTemplateForm>;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
    };

export function QuoteBrandTemplateForm(props: Props) {
  const { mode = 'CREATE', defaultValues = DEFAULT_VALUES } = props;

  return (
    <BrandTemplateFormBase<IQuoteBrandTemplateForm>
      config={{
        variant: 'SCOPED',
        featureContext: FEATURE_CONTEXT.QUOTE_BRAND_TEMPLATE,
        cancelTo: '..',
        customerScope: {
          minLevel: CUSTOMER_LEVEL.ROOT,
          filterOption: (level) => level <= CUSTOMER_LEVEL.N1,
        },
      }}
      resolver={zodResolver(quoteBrandTemplateFormSchema)}
      defaultValues={defaultValues}
      mode={mode}
      submission={
        props.mode === 'READ_ONLY'
          ? undefined
          : {
              isLoading: props.isLoading,
              errors: props.errors,
              onSubmit: (
                formData: IQuoteBrandTemplateForm,
                criteria: ICriteria[],
                formState,
              ) =>
                props.onSubmit?.(
                  QuoteBrandTemplateHelpers.fromFormToPayload(
                    formData,
                    criteria,
                  ),
                  formState as {
                    criteria: ReturnType<
                      UseFormGetFieldState<IQuoteBrandTemplatePayload>
                    >;
                  },
                ),
            }
      }
    />
  );
}
